import React from 'react';
import styled from '@emotion/styled';
import { CircleTopicFormat } from '@modernhealth/circles-graphql';
import {
    CircleCard,
    CircleCardLink,
    SeriesAccessType,
    SessionTime,
} from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { BodyTextSmall, BodyTextXSmall } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { Link } from 'react-router-dom';

import { isSeriesMembersOnly } from '../helpers/isSeriesMembersOnly';

import { TranslatedSeriesAccess } from './TranslatedSeriesAccess';
import { CircleCardHeaderLink } from './CircleCardHeaderLink';

interface CircleSeriesCardEyebrowProps {
    name: string;
    accessType?: string | null;
    slug?: string | null;
}

export interface CircleSessionStylizedCardProps {
    id: string;
    session: {
        startTime?: string | null;
        facilitators: Array<{ photo?: string | null } | null>;
    };
    topic: {
        name: string;
        slug?: string | null;
        format: CircleTopicFormat;
        duration?: number;
    };
    series?: CircleSeriesCardEyebrowProps;
    showAccess?: boolean;
}

const SubHeader = styled.span({
    display: 'flex',
    alignItems: 'center',
});

const EyebrowLink: React.FC<{ name: string; to?: string }> = ({ name, to }) =>
    to ? (
        <Link
            css={{
                zIndex: 10,
                fontSize: 'inherit',
                fontWeight: 500,
                color: colors.gray65,
                '&:hover': {
                    textDecoration: 'underline',
                    color: colors.gray85,
                },
            }}
            to={to}
        >
            {name}
        </Link>
    ) : (
        <span
            css={{
                fontSize: 'inherit',
                fontWeight: 500,
                color: colors.gray65,
            }}
        >
            {name}
        </span>
    );

export const CirclesSeriesEyebrow: React.FC<{
    series: CircleSeriesCardEyebrowProps;
    showAccess?: boolean;
    showSeriesLink?: boolean;
}> = ({ series, showAccess = false, showSeriesLink = false }) => {
    const seriesName = series?.name;
    const isMemberOnly = showAccess && isSeriesMembersOnly(series);

    return (
        <SubHeader>
            {isMemberOnly ? (
                <BodyTextXSmall as="span" fontColor="violet55" css={{ fontWeight: 500 }}>
                    <TranslatedSeriesAccess
                        accessType={SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY}
                    />
                </BodyTextXSmall>
            ) : null}
            {seriesName && showSeriesLink && isMemberOnly ? (
                <BodyTextXSmall
                    as="span"
                    fontColor="violet55"
                    css={{ marginRight: 4, marginLeft: 4, fontWeight: 500 }}
                >
                    •
                </BodyTextXSmall>
            ) : null}
            {seriesName && showSeriesLink ? (
                <>
                    <EyebrowLink
                        name={seriesName.toUpperCase()}
                        to={series.slug ? `/circles/series/${series.slug}` : undefined}
                    />
                </>
            ) : null}
        </SubHeader>
    );
};

export const CircleSessionCard: React.FC<CircleSessionStylizedCardProps> = ({
    session,
    topic,
    series,
    children,
    id,
    showAccess,
}) => {
    const photos = filterNullish(session.facilitators.map(facilitator => facilitator?.photo) ?? []);

    const body = (
        <BodyTextSmall as="span">
            <SessionTime time={session.startTime!} />
        </BodyTextSmall>
    );

    return (
        <CircleCard
            format={topic.format}
            photos={photos}
            eyebrow={
                series ? (
                    <CirclesSeriesEyebrow series={series} showAccess={showAccess} showSeriesLink />
                ) : null
            }
            header={
                <CircleCardHeaderLink
                    name={topic.name}
                    to={`/circles/${topic.slug}`}
                    aria-describedby={children ? `${id}-description` : null}
                />
            }
            body={body}
        >
            {children ? (
                <CircleCardLink
                    component={<span id={`${id}-description`} aria-hidden css={{ marginTop: 8 }} />}
                >
                    {children}
                </CircleCardLink>
            ) : null}
        </CircleCard>
    );
};
